<script setup lang="ts">
/**
 * Dashboard de ventas (§37).
 *
 * Reemplaza a la tabla de totales que había acá: la pregunta con la que se abre
 * esta pantalla es "cómo viene el año", y eso se lee en cuatro números y una
 * curva, no en un listado. El detalle por cliente se mudó a su propia pantalla
 * —Client sales—, donde vive sin período, que es como se mira una deuda.
 *
 * **Es del centro donde estás parado**, sin selector: el dashboard de la cuenta
 * consolidada no existe todavía, y un desplegable que casi siempre se deja en el
 * mismo valor es un paso de más en la pantalla que más se abre.
 *
 * Los importes salen del precio congelado de cada reserva (§25): reabrir el
 * reporte de agosto en diciembre da los mismos números aunque los precios hayan
 * cambiado tres veces en el medio.
 */
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance'
import { useTheme } from 'vuetify'
import { hexToRgb } from '@core/utils/colorConverter'
import {
  getOutstandingSummary,
  getSalesReport,
  type OutstandingRow,
  type OutstandingSummary,
  type SalesReport,
} from '@/api/reports'
import AlertBox from '@/components/AlertBox.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage } from '@/composables/useSnackbar'

definePage({ meta: { requiresAuth: true } })

const { formatCurrency, formatDate } = usePreferences()
const { currentId } = useCurrentDiveCenter()

const vuetifyTheme = useTheme()

const isoDay = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const today = isoDay(new Date())

const daysAgo = (n: number) => {
  const d = new Date()

  d.setDate(d.getDate() - n)

  return isoDay(d)
}

/*
 * Atajos de rango, en días. Son los cortes con los que se mira un negocio de
 * temporada —el día, la semana, la quincena, el mes— y evitan el paso más
 * molesto de la pantalla: abrir el calendario y contar hacia atrás para algo que
 * se pide todos los días.
 *
 * `days - 1` porque los dos extremos son inclusive: "7 días" es hoy y los seis
 * anteriores, no hoy y los siete. Con eso, 1 es hoy solo.
 */
const RANGE_PRESETS = [1, 7, 30, 'year'] as const

type RangePreset = typeof RANGE_PRESETS[number]

/*
 * El de un día se llama "Today" y no "Last 1 days": es el cierre de caja de la
 * jornada, la pregunta más frecuente de todas, y nombrarlo por su duración lo
 * volvería el único de los cuatro que hay que traducir mentalmente.
 */
const presetLabel = (p: RangePreset) =>
  (p === 'year' ? 'This year' : p === 1 ? 'Today' : `Last ${p} days`)

/** El año en curso, ENTERO: el default de esta pantalla. */
const startOfYear = () => `${new Date().getFullYear()}-01-01`
const endOfYear = () => `${new Date().getFullYear()}-12-31`

/** El rango que produce cada atajo. */
/*
 * "This year" llega hasta el 31/12 y no hasta hoy: el panel de lo pendiente
 * muestra lo reservado hacia adelante, y un período que termina hoy dejaría
 * afuera justo eso.
 */
const presetRange = (p: RangePreset): { from: string; to: string } =>
  (p === 'year' ? { from: startOfYear(), to: endOfYear() } : { from: daysAgo(p - 1), to: today })

/*
 * Hoy por defecto, para todos.
 *
 * La pantalla se abre sobre todo para cerrar el día, no para revisar el mes, y
 * el rango corto además es el que responde más rápido. Para staff, que solo
 * puede pedir un día, deja de ser una excepción y pasa a ser el mismo default
 * que el resto.
 */
/*
 * El año en curso por defecto.
 *
 * Es la escala en la que se piensa un negocio de temporada: con "hoy" por
 * defecto el dashboard abría casi siempre en cero y no decía nada. El cierre del
 * día lo sigue contestando el atajo "Today", a un click.
 */
const from = ref(startOfYear())
const to = ref(endOfYear())

/*
 * El rango es UN campo, no dos.
 *
 * Eran dos `AppDateTimePicker` sueltos dentro de un contenedor flex, y ahí
 * colapsaban a dos cuadraditos vacíos: el input de flatpickr es
 * `position: absolute` (ver `.flat-picker-custom-style` en el componente de la
 * plantilla), así que el campo no tiene ancho propio y un flex item sin ancho se
 * encoge hasta desaparecer. Con `mode: 'range'` es un solo campo —el mismo
 * patrón que Seasons y Closed days— y de paso se elige el período en un gesto en
 * vez de dos.
 */
const RANGE_SEPARATOR = ' to '

const rangeValue = computed(() => [from.value, to.value].filter(Boolean).join(RANGE_SEPARATOR))

/**
 * 🪤 El valor a medio elegir NO se escribe en el modelo.
 *
 * Flatpickr emite `onChange` también en el primer click de un rango, con la
 * fecha de inicio sola. La versión anterior completaba ahí el rango
 * (`to = parts[1] ?? parts[0]`) y eso lo rompía: el modelo quedaba con un rango
 * CERRADO, volvía a entrar por `:model-value`, y flatpickr lo leía como una
 * selección completa — así que el segundo click no cerraba el rango sino que
 * empezaba uno nuevo. De ahí el "hay que hacer doble click".
 *
 * Ahora la emisión de una sola parte se ignora y la selección a medias vive solo
 * dentro de flatpickr, que es de quien es. El modelo se toca cuando el rango
 * está completo, y el caso de un día suelto lo cierra `onClose`.
 */
const onRangeChange = (value: unknown) => {
  const parts = String(value ?? '').split(RANGE_SEPARATOR).map(p => p.trim()).filter(Boolean)

  if (parts.length < 2)
    return

  from.value = parts[0]
  to.value = parts[1]
}

/**
 * Un solo día: se elige clickeando una fecha y cerrando el calendario.
 *
 * Sin esto, ese gesto no llegaría nunca al modelo — `onChange` ignora las
 * emisiones de una parte, y flatpickr no vuelve a avisar. Al cerrar con una sola
 * fecha seleccionada se toma como un rango de un día, que es lo que el gesto
 * quiere decir.
 */
const onRangeClose = (selectedDates: Date[]) => {
  if (selectedDates.length !== 1)
    return

  const day = isoDay(selectedDates[0])

  from.value = day
  to.value = day
}

/** Qué atajo corresponde al rango actual, para marcarlo. `null` si es a medida. */
const activePreset = computed(() =>
  RANGE_PRESETS.find(p => {
    const r = presetRange(p)

    return from.value === r.from && to.value === r.to
  }) ?? null)

/*
 * Los atajos viven DENTRO del desplegable del calendario.
 *
 * Es el patrón de los date-range pickers que traen la columna de "Last 7 days /
 * Last 30 days" al costado: elegir el período es una sola tarea y tiene que
 * resolverse en un solo lugar, sin cerrar el calendario para apretar un botón
 * que está afuera.
 *
 * Flatpickr no tiene atajos propios, pero sí un hook `onReady` que entrega su
 * `calendarContainer`: alcanza con colgarle nuestros botones. Así no hay que
 * cambiar de librería —el resto del portal usa este mismo componente— ni
 * envolver un date picker en otro.
 *
 * 🪤 Van ARRIBA y no en una columna a la izquierda, que sería lo más parecido al
 * diseño de referencia: con `showMonths: 2` flatpickr escribe un `width` EN LÍNEA
 * sobre el calendario, calculado a partir del ancho de los días, y lo rehace en
 * cada apertura. Una columna lateral queda fuera de ese ancho, así que habría que
 * ganarle con `!important` a un valor que se reescribe solo — un pulso que se
 * pierde en cuanto flatpickr cambie cómo lo calcula. Una fila arriba entra en el
 * ancho que ya tiene y no pelea con nada.
 */
let fpInstance: FlatpickrInstance | null = null
let shortcutButtons: HTMLButtonElement[] = []

const paintActiveShortcut = () => {
  for (const btn of shortcutButtons)
    btn.classList.toggle('bt-fp-shortcut--on', String(activePreset.value) === btn.dataset.preset)
}

const mountShortcuts = (fp: FlatpickrInstance) => {
  fpInstance = fp

  // `onReady` puede dispararse más de una vez si el componente se redibuja.
  if (fp.calendarContainer.querySelector('.bt-fp-shortcuts'))
    return

  const bar = document.createElement('div')

  bar.className = 'bt-fp-shortcuts'

  shortcutButtons = RANGE_PRESETS.map(preset => {
    const btn = document.createElement('button')

    // `type="button"`: dentro de un formulario, un botón sin tipo lo envía.
    btn.type = 'button'
    btn.className = 'bt-fp-shortcut'
    btn.dataset.preset = String(preset)
    btn.textContent = presetLabel(preset)
    btn.addEventListener('click', () => applyPreset(preset))
    bar.appendChild(btn)

    return btn
  })

  fp.calendarContainer.prepend(bar)
  paintActiveShortcut()
}

const rangeConfig = {
  mode: 'range' as const,
  showMonths: 2,
  dateFormat: 'Y-m-d',
  onReady: (_dates: Date[], _str: string, fp: FlatpickrInstance) => mountShortcuts(fp),
  // Al reabrir, el rango pudo cambiar desde afuera: se repinta cuál está activo.
  onOpen: () => paintActiveShortcut(),
  onClose: (selectedDates: Date[]) => onRangeClose(selectedDates),
}



const report = ref<SalesReport | null>(null)

/*
 * Lo que falta cobrar, aparte del reporte y a propósito.
 *
 * El reporte mide un período CERRADO —recibos cobrados (§40)— y esto es un saldo
 * del presente: una deuda de marzo sigue siendo deuda en agosto, y meterla en el
 * total de un rango la escondería en cuanto se elija otro.
 */
const outstanding = ref<OutstandingSummary | null>(null)

const loading = ref(false)
const error = ref('')

const load = async () => {
  if (!currentId.value)
    return

  loading.value = true
  error.value = ''
  try {
    const res = await getSalesReport({
      from: from.value,
      to: to.value,
      diveCenterId: currentId.value,
    })

    report.value = res.report
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load the sales report.')
    report.value = null
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  load()
  loadOutstanding()
})

/*
 * Cambiar de centro desde la cabecera recarga: esta es una pantalla de la
 * CUENTA, así que el `:key` del `RouterView` no la remonta (§23.3).
 */
watch(currentId, () => {
  load()
  loadOutstanding()
})

/*
 * Un atajo corre el reporte solo: elegir "This year" y después tener que apretar
 * "Run report" convierte un gesto en dos para lo único que se hace siempre
 * igual. El botón se queda para cuando se edita el rango a mano.
 *
 * `setDate` con `triggerChange: false` a propósito: las fechas ya se escribieron
 * arriba, y dejar que flatpickr dispare su `onChange` haría que `onRangeChange`
 * las volviera a parsear desde el texto para llegar al mismo valor.
 */
const applyPreset = (preset: RangePreset) => {
  const range = presetRange(preset)

  from.value = range.from
  to.value = range.to

  fpInstance?.setDate([from.value, to.value], false)
  paintActiveShortcut()
  fpInstance?.close()

  load()
  // El rango define hasta dónde llega "Booked ahead", así que el panel también.
  loadOutstanding()
}

const money = (amount: number) => formatCurrency(amount, report.value?.currency)

// ---- Las métricas ----

/*
 * Qué se mide, y por qué estas.
 *
 * Desde §40 el reporte sale de los RECIBOS COBRADOS, así que acá no hay
 * "pendiente": todo lo que aparece ya entró. Lo que falta cobrar no es una venta
 * de este período sino un saldo de hoy, y vive en Client sales.
 *
 *   - **Revenue** — lo cobrado en el período. El titular.
 *   - **Receipts** — cuántos comprobantes se emitieron.
 *   - **Average receipt** — cuánto deja cada cobro. Dos centros con la misma
 *     facturación y distinto ticket son dos negocios distintos.
 *   - **Clients** — cuánta gente distinta pagó; con el ticket dice si el mes
 *     vino de muchos o de pocos.
 *   - **Top services** — de dónde sale la plata, que es lo que decide qué
 *     empujar, y no estaba en ninguna pantalla.
 */
const totals = computed(() => report.value?.totals)

/** Lo que deja cada comprobante. `null` sin cobros: 0 diría que se cobra a cero. */
const averageReceipt = computed(() => {
  const t = totals.value

  if (!t || t.bookings <= 0) return null

  return t.total / t.bookings
})

const topServices = computed(() => (report.value?.byService ?? []).slice(0, 6))

/** El más vendido manda la escala de las barritas de la tarjeta. */
const topServiceMax = computed(() =>
  topServices.value.reduce((max, s) => Math.max(max, s.total), 0))

const tiles = computed(() => {
  const t = totals.value

  if (!t) return []

  return [
    { title: 'Revenue', value: money(t.total), icon: 'ri-money-euro-circle-line', color: 'success' },
    { title: 'Receipts', value: String(t.bookings), icon: 'ri-receipt-line', color: 'primary' },
    {
      title: 'Average receipt',
      value: averageReceipt.value == null ? '—' : money(averageReceipt.value),
      icon: 'ri-price-tag-3-line',
      color: 'info',
    },
    { title: 'Clients', value: String(report.value?.clients ?? 0), icon: 'ri-user-heart-line', color: 'warning' },
  ]
})

// ---- La curva ----

/** 'YYYY-MM' → 'Mar 26', que es como se lee un eje de meses. */
const monthLabel = (month: string) => {
  const [y, m] = month.split('-').map(Number)

  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('en', { month: 'short', year: '2-digit', timeZone: 'UTC' })
}

const chartSeries = computed(() => [
  { name: 'Revenue', data: (report.value?.byMonth ?? []).map(m => m.total) },
])

/*
 * Una sola serie: todo lo que entra al reporte ya está cobrado, así que la
 * columna del mes es el mes entero. La barra apilada de antes distinguía cobrado
 * de pendiente, y con recibos esa distinción dejó de existir acá.
 */
const chartOptions = computed(() => {
  const theme = vuetifyTheme.current.value.colors
  const variables = vuetifyTheme.current.value.variables
  const labelColor = `rgba(${hexToRgb(String(variables['theme-on-surface']))},${variables['medium-emphasis-opacity']})`
  const borderColor = `rgba(${hexToRgb(String(variables['border-color']))},${variables['border-opacity']})`

  return {
    chart: { type: 'bar', toolbar: { show: false }, parentHeightOffset: 0 },
    colors: [theme.primary],
    plotOptions: { bar: { borderRadius: 6, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: { borderColor, strokeDashArray: 6, padding: { top: -10 } },
    xaxis: {
      categories: (report.value?.byMonth ?? []).map(m => monthLabel(m.month)),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: labelColor } },
    },
    yaxis: { labels: { style: { colors: labelColor } } },
    states: { hover: { filter: { type: 'none' } } },
  }
})

// ---- Lo que falta cobrar ----

/*
 * Reemplaza al listado de últimas ventas.
 *
 * Sobre una venta ya cobrada no hay nada que hacer; sobre una impaga sí, y es
 * justo la fila desde la que se quiere emitir el recibo. Los cobrados están a un
 * click, en su propia pantalla.
 *
 * 🪤 Se pagina, filtra y ordena **en el servidor**. Un centro con movimiento tiene
 * cientos de reservas sin cobrar: traerlas todas para recortar en el navegador
 * era exactamente lo que hacía lenta la pantalla. Los totales de las tarjetas
 * siguen siendo de TODAS —los calcula el backend antes de recortar—, porque
 * cuánto te deben no puede depender de en qué página estés parado.
 */
const pendingFilter = ref<'overdue' | 'upcoming' | 'all'>('overdue')
const pendingSort = ref<'date' | 'total'>('date')
const pendingDir = ref<'asc' | 'desc'>('asc')
const pendingPage = ref(1)
const pendingPerPage = ref(25)
const pendingLoading = ref(false)

const loadOutstanding = async () => {
  if (!currentId.value)
    return

  pendingLoading.value = true
  try {
    /*
     * El panel es accesorio: si falla, el reporte de arriba se muestra igual y
     * solo se pierde este bloque.
     */
    outstanding.value = await getOutstandingSummary(currentId.value, {
      until: to.value,
      filter: pendingFilter.value,
      sort: pendingSort.value,
      dir: pendingDir.value,
      limit: pendingPerPage.value,
      skip: (pendingPage.value - 1) * pendingPerPage.value,
    })
  }
  catch {
    outstanding.value = null
  }
  finally {
    pendingLoading.value = false
  }
}

// Cambiar el filtro, el orden o el tamaño de página vuelve a la primera: la
// página cuatro de otra lista no quiere decir nada.
watch([pendingFilter, pendingSort, pendingDir, pendingPerPage], () => {
  pendingPage.value = 1
  loadOutstanding()
})

watch(pendingPage, loadOutstanding)

const pendingRows = computed<OutstandingRow[]>(() => outstanding.value?.rows ?? [])

/*
 * El orden lo maneja `VDataTableServer` y se traduce a lo que espera la API.
 *
 * Solo dos columnas son ordenables, que son las dos formas de decidir por dónde
 * empezar: la más vieja o la más grande. Quitar la ordenación deja el default
 * del backend —vencidas primero, de la más antigua a la más nueva—, que es el
 * orden en que se reclama.
 */
const sortByModel = computed(() => [{ key: pendingSort.value, order: pendingDir.value }])

const onSort = (value: { key: string; order?: boolean | 'asc' | 'desc' }[]) => {
  const first = value[0]

  if (!first) return

  pendingSort.value = first.key === 'total' ? 'total' : 'date'
  pendingDir.value = first.order === 'desc' ? 'desc' : 'asc'
}

const pendingHeaders = [
  { title: 'Status', key: 'status', sortable: false, width: 76 },
  { title: 'Activity date', key: 'date' },
  { title: 'Client', key: 'clientName', sortable: false },
  { title: 'Activity', key: 'title', sortable: false },
  { title: 'Seats', key: 'seats', sortable: false, align: 'end' as const },
  { title: 'Amount', key: 'total', align: 'end' as const },
  { title: '', key: 'actions', sortable: false, width: 56, align: 'end' as const },
]

/**
 * El estado de cada fila, en color.
 *
 * Tres, y ninguno se deduce del importe: una vencida hay que reclamarla, una por
 * venir hay que esperarla, y una sin precio no se puede cobrar hasta que alguien
 * le ponga uno.
 */
const rowStatus = (row: OutstandingRow) => {
  if (row.total == null)
    return { label: 'No price', color: 'secondary', icon: 'ri-question-line' }

  if (row.overdue)
    return { label: 'Overdue', color: 'error', icon: 'ri-alarm-warning-line' }

  return { label: 'Upcoming', color: 'info', icon: 'ri-calendar-schedule-line' }
}

/**
 * Hace cuánto está la deuda más vieja, en palabras.
 *
 * El número solo no dice si es un olvido de ayer o algo de hace medio año, y esa
 * es la diferencia entre revisarlo y salir a reclamar.
 */
const overdueAge = computed(() => {
  const oldest = outstanding.value?.oldestOverdue

  if (!oldest) return ''

  const days = Math.floor((Date.now() - new Date(`${oldest}T00:00:00Z`).getTime()) / 86_400_000)

  if (days < 1) return 'since today'
  if (days < 31) return `oldest ${days} day${days === 1 ? '' : 's'} ago`

  const months = Math.floor(days / 30)

  return `oldest ${months} month${months === 1 ? '' : 's'} ago`
})

const owedMoney = (amount: number) => formatCurrency(amount, outstanding.value?.currency)
</script>

<template>
  <div>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard class="mb-6">
      <VCardText>
        <!--
          Grilla y no `d-flex`: `AppDateTimePicker` no tiene ancho propio (su
          input es `position: absolute`), así que como flex item se encoge hasta
          quedar en un cuadradito vacío. En una `VCol` el ancho lo pone la
          columna.
        -->
        <VRow align="center">
          <VCol
            cols="12"
            md="5"
          >
            <AppDateTimePicker
              :model-value="rangeValue"
              label="Period"
              :config="rangeConfig"
              @update:model-value="onRangeChange"
            />
          </VCol>

          <VCol
            cols="12"
            md="3"
          >
            <VBtn
              :loading="loading"
              block
              @click="() => { load(); loadOutstanding() }"
            >
              Run report
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Primera carga: sin esto la pantalla queda con el selector de fechas y
         nada más, y no se distingue de un centro sin ventas. -->
    <div
      v-if="loading && !report"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <template v-else-if="report && totals">
      <VRow class="mb-2">
        <!--
          La tarjeta grande: lo vendido, con el desglose al lado y la curva
          debajo. Es el bloque del que se lee "cómo viene el año" de un vistazo,
          y por eso ocupa el ancho que ocupa.
        -->
        <VCol
          cols="12"
          md="8"
        >
          <VCard class="h-100">
            <VCardItem>
              <VCardTitle>Revenue</VCardTitle>
              <VCardSubtitle>{{ formatDate(from) }} — {{ formatDate(to) }}</VCardSubtitle>
            </VCardItem>

            <VCardText>
              <VRow>
                <VCol
                  cols="12"
                  sm="4"
                >
                  <div class="text-h3 mb-4">
                    {{ money(totals.total) }}
                  </div>

                  <div class="text-body-2 text-medium-emphasis">
                    <!-- Todo lo que entra acá ya se cobró: el recibo ES la venta
                         (§40). Lo que falta cobrar es un saldo de hoy y vive en
                         Client sales, no en un período. -->
                    Collected across {{ totals.bookings }} receipt(s)
                  </div>
                </VCol>

                <VCol
                  cols="12"
                  sm="8"
                >
                  <!-- Una sola columna por mes no es una curva: con un mes solo
                       el gráfico no dice nada que no diga el número de al lado. -->
                  <VueApexCharts
                    v-if="report.byMonth.length > 1"
                    type="bar"
                    :height="240"
                    :options="chartOptions"
                    :series="chartSeries"
                  />
                  <div
                    v-else
                    class="d-flex align-center justify-center h-100 text-medium-emphasis text-body-2"
                  >
                    Pick a longer period to see the monthly trend.
                  </div>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>
        </VCol>

        <!-- De dónde sale la plata. Es lo que decide qué conviene empujar, y no
             estaba en ninguna pantalla. -->
        <VCol
          cols="12"
          md="4"
        >
          <VCard class="h-100">
            <VCardItem>
              <VCardTitle>Top services</VCardTitle>
              <VCardSubtitle>What brought the money in</VCardSubtitle>
            </VCardItem>
            <VCardText>
              <div
                v-for="s in topServices"
                :key="s.label"
                class="mb-4"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-body-2 text-truncate me-2">{{ s.label }}</span>
                  <span class="text-body-2 font-weight-medium">{{ money(s.total) }}</span>
                </div>
                <VProgressLinear
                  :model-value="topServiceMax ? (s.total / topServiceMax) * 100 : 0"
                  :color="s.kind === 'rental' ? 'info' : 'primary'"
                  height="6"
                  rounded
                />
              </div>

              <div
                v-if="!topServices.length"
                class="text-body-2 text-medium-emphasis text-center py-6"
              >
                Nothing sold in this period.
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <VRow class="mb-2">
        <VCol
          v-for="tile in tiles"
          :key="tile.title"
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText class="d-flex align-center ga-4">
              <VAvatar
                :color="tile.color"
                variant="tonal"
                rounded
                size="42"
              >
                <VIcon :icon="tile.icon" />
              </VAvatar>
              <div>
                <div class="text-h5">
                  {{ tile.value }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ tile.title }}
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!--
        Lo que falta cobrar. Va DEBAJO del período y visualmente aparte porque no
        es del período: es un saldo de hoy (§41). Mezclarlo con los totales de
        arriba era lo que obligaba a explicar por qué la suma no cerraba.
      -->
      <VRow
        v-if="outstanding"
        class="mb-2"
      >
        <VCol
          cols="12"
          sm="6"
        >
          <VCard>
            <VCardText class="d-flex align-center ga-4">
              <VAvatar
                color="error"
                variant="tonal"
                rounded
                size="42"
              >
                <VIcon icon="ri-alarm-warning-line" />
              </VAvatar>
              <div>
                <div class="text-h5">
                  {{ owedMoney(outstanding.overdue) }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  Owed — activities already done · {{ overdueAge }}
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          sm="6"
        >
          <VCard>
            <VCardText class="d-flex align-center ga-4">
              <VAvatar
                color="info"
                variant="tonal"
                rounded
                size="42"
              >
                <VIcon icon="ri-calendar-schedule-line" />
              </VAvatar>
              <div>
                <div class="text-h5">
                  {{ owedMoney(outstanding.upcoming) }}
                </div>
                <!-- Es una previsión y hay que decir hasta cuándo: sin la fecha,
                     el número no se puede interpretar. -->
                <div class="text-caption text-medium-emphasis">
                  Booked ahead — unpaid, up to {{ formatDate(to) }}
                </div>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <VRow>
        <VCol cols="12">
          <VCard>
            <VCardItem>
              <VCardTitle>To collect</VCardTitle>
              <VCardSubtitle>Bookings with nothing charged yet</VCardSubtitle>
              <template #append>
                <VChipGroup
                  v-model="pendingFilter"
                  mandatory
                  selected-class="text-primary"
                >
                  <VChip value="overdue">
                    Overdue
                  </VChip>
                  <VChip value="upcoming">
                    Upcoming
                  </VChip>
                  <VChip value="all">
                    All
                  </VChip>
                </VChipGroup>
              </template>
            </VCardItem>

            <!--
              La tabla de la plantilla y no una `VTable` a mano: trae su
              paginador —el mismo de Clients y Receipts—, su barra de carga y el
              orden por columna. La versión anterior los reimplementaba y se veía
              distinta del resto del portal.

              El paginado y el orden son del SERVIDOR: `@update:options` se
              ignora porque los modelos ya disparan la recarga, y `items-length`
              es el total del backend, no el largo de la página.
            -->
            <VDataTableServer
              v-model:page="pendingPage"
              v-model:items-per-page="pendingPerPage"
              :items="pendingRows"
              :items-length="outstanding?.total ?? 0"
              :headers="pendingHeaders"
              :loading="pendingLoading"
              :sort-by="sortByModel"
              :items-per-page-options="[10, 25, 50]"
              item-value="bookingId"
              class="text-no-wrap"
              @update:sort-by="onSort"
              @update:options="() => {}"
            >
              <template #item.status="{ item }">
                <VTooltip
                  :text="rowStatus(item).label"
                  location="top"
                >
                  <template #activator="{ props: tip }">
                    <VAvatar
                      v-bind="tip"
                      :color="rowStatus(item).color"
                      variant="tonal"
                      rounded
                      size="32"
                    >
                      <VIcon
                        :icon="rowStatus(item).icon"
                        size="18"
                      />
                    </VAvatar>
                  </template>
                </VTooltip>
              </template>

              <template #item.date="{ item }">
                {{ formatDate(item.date) }}
                <span
                  v-if="item.time"
                  class="text-medium-emphasis"
                >· {{ item.time }}</span>
              </template>

              <template #item.clientName="{ item }">
                <!-- A su ficha, abierta en actividades: la pregunta que sigue a
                     ver una deuda es qué más hizo esa persona. -->
                <RouterLink
                  v-if="item.clientId"
                  :to="`/clients/${item.clientId}`"
                  class="font-weight-medium"
                >
                  {{ item.clientName }}
                </RouterLink>
                <span
                  v-else
                  class="font-weight-medium"
                >{{ item.clientName }}</span>
              </template>

              <template #item.title="{ item }">
                <span class="text-medium-emphasis">{{ item.title }}</span>
              </template>

              <template #item.total="{ item }">
                <!-- Sin precio en el catálogo no se inventa un cero: se dice, y
                     se cobra poniéndolo a mano en el recibo (§25). -->
                <span
                  v-if="item.total == null"
                  class="text-medium-emphasis"
                >No price</span>
                <span
                  v-else
                  class="font-weight-medium"
                >{{ owedMoney(item.total) }}</span>
              </template>

              <template #item.actions="{ item }">
                <VBtn
                  v-if="item.clientId"
                  icon="ri-receipt-line"
                  variant="text"
                  size="small"
                  color="primary"
                  title="Create a receipt for this client"
                  :to="`/sales/receipts/new?clientId=${item.clientId}`"
                />
              </template>

              <template #no-data>
                <div class="text-center text-medium-emphasis py-8">
                  Nothing to collect here.
                </div>
              </template>
            </VDataTableServer>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>

<style lang="scss">
/* Los atajos que se inyectan dentro del calendario de flatpickr. */
.bt-fp-shortcuts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 10px 4px;
}

.bt-fp-shortcut {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 999px;
  font-size: 0.75rem;
  padding: 3px 10px;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }

  &--on {
    background: rgb(var(--v-theme-primary));
    border-color: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-on-primary));
  }
}
</style>
